import { useEffect, useState } from "react";
import { useApiClient } from "../../../../data/clients/ApiClient";
import { PageQuery } from "../../../../utils/models/pageQuery";
import { getActAsOrgId } from "../../../../contexts/actAsStore";

const CHAIN_LOGO_CLASSIFICATION = "11";
// Per-organization cache key ("hasChainLogos:{orgId}"). The legacy global
// "hasChainLogos" key is intentionally abandoned (never read, never written):
// it was shared across every vendor/act-as session in the same browser, so
// one org's answer leaked into another's — e.g. an admin acting as a
// chain-less vendor cached "false" and the tab then stayed hidden for a
// chain vendor on the same machine.
const HAS_CHAIN_LOGOS_KEY_PREFIX = "hasChainLogos:";

export function useHasChainLogos(
  role?: number,
  isVendor?: boolean,
  organizationId?: number
): boolean {
  const [hasChainLogos, setHasChainLogos] = useState<boolean>(false);
  const apiClient = useApiClient();

  // Admin always sees the chain-logo UI — but the decision must NOT be an
  // early return above the useEffect: when an admin exits act-as-vendor, the
  // warm profile cache flips the role Vendor→Admin IN-PLACE on the mounted
  // logo list (no remount), and a render with fewer hooks than the previous
  // one is a React invariant violation → RouteErrorView. Hooks are therefore
  // declared unconditionally; the admin shortcut lives in the effect body and
  // the return expression instead.
  const isAdmin = role === 2;

  // An ACTIVE impersonation is always admin-driven (the API rejects everyone
  // else), so the admin shortcut extends to act-as sessions: the admin keeps
  // the chain-logo tab while acting as a vendor instead of depending on the
  // vendor-scoped probe. Note the swapped profile carries role=Vendor during
  // act-as, so the role check alone cannot see the underlying admin.
  const isActingAsVendor = getActAsOrgId() != null;

  // getAllLogos is act-as-scoped at the ApiClient choke point, so the cache
  // must be keyed by the org whose logos the lookup actually answers for:
  // the impersonated org while acting-as, otherwise the user's own org.
  const scopeOrgId = getActAsOrgId() ?? organizationId;

  useEffect(() => {
    // Admin (incl. act-as) never needs the lookup; non-vendors never see
    // chain logos.
    if (isAdmin || isActingAsVendor || !isVendor) {
      setHasChainLogos(false);
      return;
    }

    const cacheKey =
      scopeOrgId !== undefined
        ? `${HAS_CHAIN_LOGOS_KEY_PREFIX}${scopeOrgId}`
        : undefined;

    // Only a cached "true" short-circuits the fetch: "true" is stable (a
    // chain vendor stays a chain vendor), while "false" must never be sticky
    // — an org whose chain logos arrive later (NAV import / backfill queue)
    // would otherwise never get the tab back. The cost of re-checking is one
    // size-1 list call per logo-list mount.
    if (cacheKey && localStorage.getItem(cacheKey) === "true") {
      setHasChainLogos(true);
      return;
    }

    // Guard against a stale response landing after the scope changed
    // (e.g. act-as entered/exited while the lookup was in flight).
    let cancelled = false;

    const checkChainLogos = async () => {
      try {
        const query: PageQuery = {
          page: { size: 1, index: 1 },
          filterBy: [
            { field: "classification", value: CHAIN_LOGO_CLASSIFICATION },
          ],
          searchTerm: "",
          sortBy: { field: "", sortDirection: undefined },
        };

        // Direct API call is simpler for a one-off useEffect side-effect.
        const response = await apiClient.getAllLogos(query);

        const hasLogos = response.results.length > 0;
        if (!cancelled) setHasChainLogos(hasLogos);
        if (hasLogos && cacheKey) {
          localStorage.setItem(cacheKey, "true");
        }
      } catch (error) {
        console.error("Failed to check for chain logos", error);
        // Default to false on error, don't save to storage so we retry next time
        if (!cancelled) setHasChainLogos(false);
      }
    };

    checkChainLogos();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, isActingAsVendor, isVendor, apiClient, scopeOrgId]);

  return isAdmin || isActingAsVendor ? true : hasChainLogos;
}
